import { WorkerInvitationStatus } from 'models/general';

// A trade typed offline has no id until the worker update uploads it, so the consent form
// holds this placeholder in its place until then.
const TEMP_TRADE_ID_PREFIX = 'temp_';

export const getTempTradeId = (tradeName: string) => `${TEMP_TRADE_ID_PREFIX}${tradeName}`;

/** The trade name inside a placeholder id, or null when the value is not a placeholder. */
export const getTempTradeName = (value: unknown): string | null =>
  typeof value === 'string' && value.startsWith(TEMP_TRADE_ID_PREFIX) ? value.slice(TEMP_TRADE_ID_PREFIX.length) : null;

const transformTrades = (beTrades, skilledTrades) => {
  const trades = [];
  const otherTradesList = [];
  beTrades.forEach(trade => {
    const matchedTrade = skilledTrades?.find(skilledTrade => skilledTrade.id === trade.id) || null;
    if (!matchedTrade) {
      otherTradesList.push({ OtherText: trade.name, isSaved: false });
    } else {
      if (matchedTrade?.masterList) {
        trades?.push(matchedTrade.id);
      } else {
        otherTradesList.push({ OtherText: matchedTrade.id, isSaved: true });
      }
    }
  });

  // Typed trades are only accepted alongside the "Other" trade — the online Trades page adds it the same way.
  // Records saved offline before that page kept it can be missing it, so add it here too.
  const otherTrade = skilledTrades?.find(trade => ['other', 'otro'].includes(trade?.name?.toLowerCase()));
  if (otherTradesList.length && otherTrade && !trades.includes(otherTrade.id)) {
    trades.push(otherTrade.id);
  }

  return { trades, otherTradesList };
};

export const getUpdateWorkerBodyFromOnboarding = (onboarding: any, { skilledTrades }: any) => {
  const { trades, otherTradesList } = transformTrades(onboarding?.trades || [], skilledTrades || []);

  let companyIds = onboarding?.temp?.employers?.map(employer => employer.value) || [];

  const body: any = {
    // Personal Information Data
    firstName: onboarding?.firstName,
    middleName: onboarding?.middleName,
    lastName: onboarding?.lastName,
    dateOfBirth: onboarding?.dateOfBirth || null,
    gender: onboarding?.gender,
    ethnicityId: onboarding?.ethnicity?.id,
    primaryLanguageId: onboarding?.primaryLanguage?.id,
    isVeteran: onboarding?.isVeteran ?? null,
    allergies: onboarding?.allergies || null,
    socialSecurityNumber: onboarding?.socialSecurityNumber,
    identificationTypeId: onboarding?.identificationType?.id,
    identificationGeographicLocationId: onboarding?.identificationGeographicLocation?.id,
    identificationNumber: onboarding?.identificationNumber,
    otherIdentificationType: onboarding?.otherIdentificationType || null,
    // Contact Information Data
    email: onboarding?.email || null,
    mobilePhoneNumber: onboarding?.mobilePhoneNumber || null,
    phoneNumber: onboarding?.phoneNumber || null,
    address: onboarding?.address || null,
    emergencyContactName: onboarding?.emergencyContactName || null,
    emergencyContactRelationship: onboarding?.emergencyContactRelationship || null,
    emergencyContactPhone: onboarding?.emergencyContactPhone || null,
    // Trades Data
    isAffiliatedToLaborUnion: onboarding?.isAffiliatedToLaborUnion ?? null,
    laborUnionNumber: onboarding?.laborUnionNumber || null,
    isSkilled: onboarding?.isSkilled ?? null,
    isSupervisor: onboarding?.isSupervisor ?? null,
    trades: trades || [],
    otherTradesList: otherTradesList || [],
    // Other
    isAddingFromTablet: true,
    projectId: onboarding?.temp?.project?.value,
    companyIds: companyIds,
    companyId: companyIds[0],

    inviteMethod: onboarding?.inviteMethod,
  };

  return body;
};

export const hasWorkerAcceptedTheProject = (employers: any[]) => {
  return employers?.length > 0 && employers.filter(e => e.workerProjectStatus !== 1).length < 1;
};

export const getEmptyPiiFieldsFromWorkerDetails = (workerDetails: any = {}) => {
  return {
    dateOfBirth: !workerDetails?.dateOfBirth,
    socialSecurityNumber: !workerDetails?.socialSecurityNumber,
    line1: !workerDetails?.address?.line1,
    line2: !workerDetails?.address?.line2,
    city: !workerDetails?.address?.city,
    state: !workerDetails?.address?.stateCode,
    zipCode: !workerDetails?.address?.zipCode,
    country: !workerDetails?.address?.country?.id,
    county: !workerDetails?.address?.county,
    borough: !workerDetails?.address?.borough,
  };
};

/**
 * True while a worker who signed in with a one-time password still has no password of their own
 * (CH-4506 / CH-4515). Such a worker is locked to the Create Password tab — the other profile
 * tabs, the account menu and every worker-facing screen stay out of reach until they set one.
 *
 * Deliberately scoped to `isOtpVerifiedLogin` rather than `invitationStatus` alone: an admin
 * viewing their own profile, or building a brand-new worker in the wizard, also has a worker
 * whose invitation is not ACCEPTED, and neither of those should be locked down.
 */
export const mustCreateWorkerPassword = (onboarding: any): boolean =>
  !!onboarding?.isOtpVerifiedLogin && onboarding?.invitationStatus !== WorkerInvitationStatus.ACCEPTED;
